/**
 * src/hooks/useVisitorCity.js
 *
 * Resolves the visitor's city from Cloudflare IP geolocation (cf-ipcity /
 * cf-iplatitude / cf-iplongitude) via GET /api/map/visitor-city. The backend
 * snaps the hint to a canonical BG city name that actually has salons, so the
 * value returned here is always safe to pass straight to ?city= — or null when
 * the hint is absent (API not proxied through Cloudflare, VPN, unknown IP) or
 * too far from any city with salons, in which case the caller keeps its own
 * default.
 *
 * The result is memoised per page session at module scope: the endpoint is
 * per-visitor and sent with `no-store`, so it must not be cached by the CDN,
 * but re-requesting it on every mount would be pure waste. Concurrent mounts
 * share one in-flight request.
 */
import { useEffect, useState } from "react";

let cachedCity;        // undefined = not resolved yet, null = no usable hint
let inFlight = null;

export function fetchVisitorCity() {
  if (cachedCity !== undefined) return Promise.resolve(cachedCity);
  if (inFlight) return inFlight;

  inFlight = fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/map/visitor-city`)
    .then((r) => r.json())
    .then((payload) => {
      cachedCity = payload?.data?.city || null;
      return cachedCity;
    })
    .catch(() => {
      // Never surface a geolocation failure to the user — the caller's own
      // default city stays in place.
      cachedCity = null;
      return null;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export default function useVisitorCity() {
  const [city, setCity] = useState(cachedCity ?? null);

useEffect(() => {
    if (cachedCity !== undefined) return; // already reflected in the initial state above
    let cancelled = false;
    fetchVisitorCity().then((value) => {
      if (!cancelled) setCity(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return city;
}
